<template>
  <PInputMenu
    ref="inputMenu"
    v-bind="props"
    v-model="modelValue"
    :items="unitList"
    value-key="key"
    label-key="value"
    :filter-fields="['value', 'alternative']"
  >
    <template v-if="$slots.leading" #leading="slotProps">
      <slot name="leading" v-bind="slotProps" />
    </template>
    <template v-if="$slots.trailing" #trailing="slotProps">
      <slot name="trailing" v-bind="slotProps" />
    </template>
  </PInputMenu>
</template>

<script setup lang="ts">
import { useTemplateRef } from "vue"
import type { ComponentExposed } from "vue-component-type-helpers"
import type { ModelModifiers } from "@nuxt/ui"
import type { InputMenuSlots } from "@nuxt/ui/components/InputMenu.vue"
import type { Units } from "@/enums/units.enum"
import { getUnitList } from "@/helper/enums/unit-helper"
import PInputMenu from "./PInputMenu.vue"
import type { PInputMenuProps } from "./input-menu"

type UnitItems = ReturnType<typeof getUnitList>
type Props = Omit<
  PInputMenuProps<UnitItems, "key">,
  | "modelValue"
  | "defaultValue"
  | "modelModifiers"
  | "items"
  | "valueKey"
  | "labelKey"
  | "filterFields"
  | "multiple"
  | "mode"
  | "createItem"
  | "ignoreFilter"
>
const props = withDefaults(defineProps<Props>(), {
  resetSearchTermOnBlur: undefined,
  resetSearchTermOnSelect: undefined,
  resetModelValueOnClear: undefined,
  highlightOnHover: undefined,
  loop: undefined,
  openOnClick: undefined,
  openOnFocus: undefined,
  open: undefined,
  defaultOpen: undefined,
  portal: undefined,
  arrow: undefined,
  disabled: undefined,
  required: undefined,
  highlight: undefined,
  fixed: undefined,
  autofocus: undefined,
  leading: undefined,
  trailing: undefined,
  loading: undefined
})
const modelValue = defineModel<Units | undefined>({ set: (value) => value ?? undefined })
const unitList = getUnitList().sort((a, b) => a.value.localeCompare(b.value, "nl"))
const inputMenu = useTemplateRef<ComponentExposed<typeof PInputMenu>>("inputMenu")

type UnitSlots = InputMenuSlots<
  UnitItems,
  "key",
  false,
  Omit<ModelModifiers, "lazy">,
  boolean | object
>
defineSlots<Pick<UnitSlots, "leading" | "trailing">>()
defineExpose({
  focus: () => inputMenu.value?.inputRef?.focus()
})
</script>
